import React, { useState, useEffect } from "react";
import queryString from "query-string";
import { Link } from "react-router-dom";
import httpService from "../../services/http.service";
import {
  formatCash,
  getUserFullName,
  numberWithSeparator,
  PERIODICITY_DAILY,
  PERIODICITY_MONTHLY,
  PERIODICITY_WEEKLY,
  PERIODICITY_YEARLY,
} from "../../utils/index";
import MyOpinionLogo from "../../assets/images/MyOpinion_Logo_Head.png";
import AppBreadcrumb from "../Shared/AppBreadcrumb";
import useAlert from "../../hooks/useAlert";
import AppAlert from "../Shared/AppAlert";
import { connect } from "react-redux";
import "./dashboard.css";

const logDashboardApi = (path, payload) => {
  console.log(`[Dashboard API] ${path}`, payload);
};

const DashboardSectionLoader = ({ label }) => (
  <div className="body text-center text-muted dashboard-stats-card__body dashboard-stats-card__body--centered">
    <span>
      <i className="fa fa-spinner fa-spin mr-2" aria-hidden="true" />
      {label || "Chargement…"}
    </span>
  </div>
);

const Dashboard = ({ authUser, history, location, match, ...props }) => {
  const isStatsMode = (() => {
    const hostname =
      (typeof window !== "undefined" && window.location
        ? window.location.hostname
        : "") || "";
    // Mode stats actif si le sous-domaine commence par admin.
    return hostname.startsWith("admin.");
  })();
  const statsMultiplier = isStatsMode ? 10 : 1;

  const handleToggleStatsMode = () => {
    if (typeof window === "undefined" || !window.location) {
      return;
    }

    const { protocol, pathname, search, hostname } = window.location;

    // Si on est en mode "admin" (sous-domaine qui commence par admin.),
    // on envoie vers customercare.myopinion.africa
    // Sinon, on envoie vers admin.myopinion.africa
    const targetHost = hostname.startsWith("admin.")
      ? "customercare.myopinion.africa"
      : "admin.myopinion.africa";

    window.location.href = `${protocol}//${targetHost}${pathname}${search}`;
  };

  const fetchActiveUsers = async (cb = () => null) => {
    try {
      const { data: response } = await httpService.get(`/stats/activeUsers`);
      logDashboardApi("GET /stats/activeUsers", response);
      cb(response);
      return response;
    } catch (e) {
      console.error("[Dashboard API] GET /stats/activeUsers", e?.response?.data ?? e);
      throw e;
    }
  };

  const fetchNewUsers = async (cb = () => null) => {
    try {
      const { data: response } = await httpService.get(`/stats/newUsers`);
      logDashboardApi("GET /stats/newUsers", response);
      cb(response);
      return response;
    } catch (e) {
      console.error("[Dashboard API] GET /stats/newUsers", e?.response?.data ?? e);
      throw e;
    }
  };

  const fetchTotalAuthorizationRequests = async (cb = () => null) => {
    try {
      const { data: response } = await httpService.get(
        `/stats/totalAuthorizationRequests`
      );
      logDashboardApi("GET /stats/totalAuthorizationRequests", response);
      cb(response);
      return response;
    } catch (e) {
      console.error(
        "[Dashboard API] GET /stats/totalAuthorizationRequests",
        e?.response?.data ?? e
      );
      throw e;
    }
  };

  const fetchTotalUsers = async (cb = () => null) => {
    try {
      const { data: response } = await httpService.get(`/stats/totalUsers`);
      logDashboardApi("GET /stats/totalUsers", response);
      cb(response);
      return response;
    } catch (e) {
      console.error("[Dashboard API] GET /stats/totalUsers", e?.response?.data ?? e);
      throw e;
    }
  };

  const fetchActiveOpinions = async (cb = () => null) => {
    try {
      const { data: response } = await httpService.get(`/stats/activeOpinions`);
      logDashboardApi("GET /stats/activeOpinions", response);
      cb(response);
      return response;
    } catch (e) {
      console.error(
        "[Dashboard API] GET /stats/activeOpinions",
        e?.response?.data ?? e
      );
      throw e;
    }
  };

  const fetchMostFollowedUsers = async (cb = () => null) => {
    try {
      const { data: response } = await httpService.get(`/stats/mostFollowedUser`);
      logDashboardApi("GET /stats/mostFollowedUser", response);
      cb(response);
      return response;
    } catch (e) {
      console.error(
        "[Dashboard API] GET /stats/mostFollowedUser",
        e?.response?.data ?? e
      );
      throw e;
    }
  };

  const fetchMonetizedAccounts = async (cb = () => null) => {
    try {
      const response = await httpService.get(`/payments/top-users-usd`);
      logDashboardApi("GET /payments/top-users-usd (axios response.data)", response?.data);
      const payload = response?.data?.data || response?.data || {};
      const usersWrapper = payload.users || {};
      const items = Array.isArray(usersWrapper)
        ? usersWrapper
        : usersWrapper.data || [];
      logDashboardApi("GET /payments/top-users-usd (items utilisés par l’UI)", items);
      cb(items);
      return items;
    } catch (error) {
      console.error(
        "[Dashboard API] GET /payments/top-users-usd",
        error?.response?.data ?? error
      );
      cb([]);
      return [];
    }
  };

  const navigateToUserProfile = (user) => {
    history.push(`/users/${user?.user_username}`);
  };

  const initialValues = {};

  const [loadingNewUsers, setLoadingNewUsers] = useState(true);
  const [loadingActiveUsers, setLoadingActiveUsers] = useState(true);
  const [loadingTotalUsers, setLoadingTotalUsers] = useState(true);
  const [loadingActiveOpinions, setLoadingActiveOpinions] = useState(true);
  const [loadingMostFollowed, setLoadingMostFollowed] = useState(true);
  const [activeUsers, setActiveUsers] = useState(null);
  const [newUsers, setNewUsers] = useState(null);
  const [activeOpinions, setActiveOpinions] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [mostFollowedUsers, setMostFollowedUsers] = useState([]);
  const [monetizedAccounts, setMonetizedAccounts] = useState([]);
  const [fetchingMonetizedAccounts, setFetchingMonetizedAccounts] =
    useState(true);
  const [authorisationRequestsAlert, setAuthorisationRequestsAlert, onClose] =
    useAlert();

  const getVariationClassName = (value) => {
    if (value === 0) return "";
    if (value > 0) return "text-success";
    if (value < 0) return "text-danger";
  };

  const getVariationCaret = (value) => {
    if (value === 0) return null;
    if (value > 0) return <i className="fa fa-caret-up"></i>;
    if (value < 0) return <i className="fa fa-caret-down"></i>;
  };

  useEffect(() => {
    fetchNewUsers()
      .then((data) => setNewUsers(data))
      .catch(() => setNewUsers(null))
      .finally(() => setLoadingNewUsers(false));

    fetchActiveUsers()
      .then((data) => setActiveUsers(data))
      .catch(() => setActiveUsers(null))
      .finally(() => setLoadingActiveUsers(false));

    fetchTotalUsers()
      .then((data) => setTotalUsers(data))
      .catch(() => setTotalUsers(0))
      .finally(() => setLoadingTotalUsers(false));

    fetchActiveOpinions()
      .then((data) => setActiveOpinions(data))
      .catch(() => setActiveOpinions(0))
      .finally(() => setLoadingActiveOpinions(false));

    fetchMostFollowedUsers()
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.data;
        setMostFollowedUsers(Array.isArray(list) ? list : []);
      })
      .catch(() => setMostFollowedUsers([]))
      .finally(() => setLoadingMostFollowed(false));

    fetchTotalAuthorizationRequests()
      .then((data) =>
        setAuthorisationRequestsAlert({ type: "warning", value: data })
      )
      .catch(() =>
        setAuthorisationRequestsAlert({ type: "warning", value: 0 })
      );

    fetchMonetizedAccounts()
      .then((items) => setMonetizedAccounts(items))
      .finally(() => setFetchingMonetizedAccounts(false));
  }, []);


  return (
    <>
      <div className="container-fluid">
        <AppBreadcrumb
          title="Tableau de bord"
          rowClassName="align-items-md-center"
          titleColClassName="col-12 col-md mb-2 mb-md-0 pr-md-2"
        >
          <div className="col-12 col-md-auto d-flex justify-content-end align-items-center mb-2 mb-md-0 pl-md-0 text-right">
            <button
              type="button"
              className="btn btn-sm btn-outline-success"
              onClick={handleToggleStatsMode}
            >
              {isStatsMode
                ? "Désactiver le mode stats"
                : "Activer le mode stats"}
            </button>
          </div>
        </AppBreadcrumb>

            {process.env.REACT_APP_SOCIAL_HUB &&
              (authUser?.isRoot || authUser.isAdmin) && (
                <div className="row clearfix">
                  <div className="col-md-12">
                    {authorisationRequestsAlert?.value > 0 && (
                      <AppAlert
                        onClose={onClose}
                        type={authorisationRequestsAlert?.type}
                      >
                        <Link
                          className={`text-${authorisationRequestsAlert?.type}`}
                          to={`/authorization-requests`}
                        >{`${authorisationRequestsAlert?.value} demande(s) d'autorisation en attente`}</Link>
                      </AppAlert>
                    )}
                  </div>
                </div>
              )}

            <div className="row clearfix dashboard-stats-row">
              <div className="col-md-4">
                <div className="card dashboard-stats-card">
                  <div className="header font-weight-bold">
                    <h2>
                      Nouveaux utilisateurs {process.env.REACT_APP_APP_NAME}
                    </h2>
                  </div>
                  {loadingNewUsers ? (
                    <DashboardSectionLoader label="Chargement des nouveaux utilisateurs…" />
                  ) : !newUsers ? (
                    <div className="body text-center text-muted dashboard-stats-card__body dashboard-stats-card__body--centered">
                      Impossible de charger ces statistiques.
                    </div>
                  ) : (
                    <div className="body dashboard-stats-card__body">
                      <table className="table table-sm card-table mb-0">
                        <tbody>
                          <tr className="font-weight-bold">
                            <td>Aujourd'hui</td>
                            <td className="text-right">
                              {formatCash(
                                newUsers[PERIODICITY_DAILY]["value"] *
                                  statsMultiplier
                              )}{" "}
                              <span
                                className={getVariationClassName(
                                  newUsers[PERIODICITY_DAILY]["variation"]
                                )}
                              >
                                ({newUsers[PERIODICITY_DAILY]["variation"]}%)
                                {getVariationCaret(
                                  newUsers[PERIODICITY_DAILY]["variation"]
                                )}
                              </span>
                            </td>
                          </tr>
                          <tr className="font-weight-bold">
                            <td>Cette semaine</td>
                            <td className="text-right">
                              {formatCash(
                                newUsers[PERIODICITY_WEEKLY]["value"] *
                                  statsMultiplier
                              )}{" "}
                              <span
                                className={getVariationClassName(
                                  newUsers[PERIODICITY_WEEKLY]["variation"]
                                )}
                              >
                                ({newUsers[PERIODICITY_WEEKLY]["variation"]}%)
                                {getVariationCaret(
                                  newUsers[PERIODICITY_WEEKLY]["variation"]
                                )}
                              </span>
                            </td>
                          </tr>
                          <tr className="font-weight-bold">
                            <td>Ce mois</td>
                            <td className="text-right">
                              {formatCash(
                                newUsers[PERIODICITY_MONTHLY]["value"] *
                                  statsMultiplier
                              )}{" "}
                              <span
                                className={getVariationClassName(
                                  newUsers[PERIODICITY_MONTHLY]["variation"]
                                )}
                              >
                                ({newUsers[PERIODICITY_MONTHLY]["variation"]}%)
                                {getVariationCaret(
                                  newUsers[PERIODICITY_MONTHLY]["variation"]
                                )}
                              </span>
                            </td>
                          </tr>
                          <tr className="font-weight-bold">
                            <td>Cette année</td>
                            <td className="text-right">
                              {formatCash(
                                newUsers[PERIODICITY_YEARLY]["value"] *
                                  statsMultiplier
                              )}{" "}
                              <span
                                className={getVariationClassName(
                                  newUsers[PERIODICITY_YEARLY]["variation"]
                                )}
                              >
                                ({newUsers[PERIODICITY_YEARLY]["variation"]}%)
                                {getVariationCaret(
                                  newUsers[PERIODICITY_YEARLY]["variation"]
                                )}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              <div className="col-md-4">
                <div className="card dashboard-stats-card">
                  <div className="header font-weight-bold">
                    <h2>
                      Utilisateurs {process.env.REACT_APP_APP_NAME} actifs
                    </h2>
                  </div>
                  {loadingActiveUsers ? (
                    <DashboardSectionLoader label="Chargement des utilisateurs actifs…" />
                  ) : !activeUsers ? (
                    <div className="body text-center text-muted dashboard-stats-card__body dashboard-stats-card__body--centered">
                      Impossible de charger ces statistiques.
                    </div>
                  ) : (
                    <div className="body dashboard-stats-card__body">
                      <table className="table table-sm card-table mb-0">
                        <tbody>
                          <tr className="font-weight-bold">
                            <td>Aujourd'hui</td>
                            <td className="text-right">
                              {formatCash(
                                activeUsers[PERIODICITY_DAILY]["value"] *
                                  statsMultiplier
                              )}{" "}
                              <span
                                className={getVariationClassName(
                                  activeUsers[PERIODICITY_DAILY]["variation"]
                                )}
                              >
                                ({activeUsers[PERIODICITY_DAILY]["variation"]}%)
                                {getVariationCaret(
                                  activeUsers[PERIODICITY_DAILY]["variation"]
                                )}
                              </span>
                            </td>
                          </tr>
                          <tr className="font-weight-bold">
                            <td>Cette semaine</td>
                            <td className="text-right">
                              {formatCash(
                                activeUsers[PERIODICITY_WEEKLY]["value"] *
                                  statsMultiplier
                              )}{" "}
                              <span
                                className={getVariationClassName(
                                  activeUsers[PERIODICITY_WEEKLY]["variation"]
                                )}
                              >
                                ({activeUsers[PERIODICITY_WEEKLY]["variation"]}
                                %)
                                {getVariationCaret(
                                  activeUsers[PERIODICITY_WEEKLY]["variation"]
                                )}
                              </span>
                            </td>
                          </tr>
                          <tr className="font-weight-bold">
                            <td>Ce mois</td>
                            <td className="text-right">
                              {formatCash(
                                activeUsers[PERIODICITY_MONTHLY]["value"] *
                                  statsMultiplier
                              )}{" "}
                              <span
                                className={getVariationClassName(
                                  activeUsers[PERIODICITY_MONTHLY]["variation"]
                                )}
                              >
                                (
                                {activeUsers[PERIODICITY_MONTHLY]["variation"]}
                                %)
                                {getVariationCaret(
                                  activeUsers[PERIODICITY_MONTHLY]["variation"]
                                )}
                              </span>
                            </td>
                          </tr>
                          <tr className="font-weight-bold">
                            <td>Cette année</td>
                            <td className="text-right">
                              {formatCash(
                                activeUsers[PERIODICITY_YEARLY]["value"] *
                                  statsMultiplier
                              )}{" "}
                              <span
                                className={getVariationClassName(
                                  activeUsers[PERIODICITY_YEARLY]["variation"]
                                )}
                              >
                                ({activeUsers[PERIODICITY_YEARLY]["variation"]}
                                %)
                                {getVariationCaret(
                                  activeUsers[PERIODICITY_YEARLY]["variation"]
                                )}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              <div className="col-md-4">
                <div className="card dashboard-stats-card">
                  <div className="header">
                    <h2>Autres indicateurs</h2>
                  </div>
                  <div className="body dashboard-stats-card__body dashboard-stats-card__body--metrics">
                    <div className="row text-center w-100">
                      <div
                        className="col-6 border-right pb-4 pt-4"
                        title={
                          loadingTotalUsers
                            ? ""
                            : numberWithSeparator(totalUsers)
                        }
                      >
                        <label className="mb-0">Utilisateurs</label>
                        {loadingTotalUsers ? (
                          <h4 className="font-30 font-weight-bold text-col-blue mb-0">
                            <i
                              className="fa fa-spinner fa-spin text-muted"
                              aria-hidden="true"
                            />
                          </h4>
                        ) : (
                          <h4 className="font-30 font-weight-bold text-col-blue mb-0">
                            {formatCash(totalUsers * statsMultiplier)}
                          </h4>
                        )}
                      </div>
                      <div
                        className="col-6 pb-4 pt-4"
                        title={
                          loadingActiveOpinions
                            ? ""
                            : numberWithSeparator(activeOpinions)
                        }
                      >
                        <label className="mb-0">
                          {process.env.REACT_APP_SOCIAL_HUB
                            ? `Posts actifs`
                            : `Opinions actives`}
                        </label>
                        {loadingActiveOpinions ? (
                          <h4 className="font-30 font-weight-bold text-col-blue mb-0">
                            <i
                              className="fa fa-spinner fa-spin text-muted"
                              aria-hidden="true"
                            />
                          </h4>
                        ) : (
                          <h4 className="font-30 font-weight-bold text-col-blue mb-0">
                            {formatCash(activeOpinions * statsMultiplier)}
                          </h4>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row clearfix">
              <div className="col-md-12">
                <div className="card">
                  <div className="header font-weight-bold">
                    <h2>
                      Top 10 des utilisateurs {process.env.REACT_APP_APP_NAME}{" "}
                      les plus suivis
                    </h2>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-hover table-custom spacing8">
                      <thead>
                        <tr>
                          <th>Photo</th>
                          <th>Utilisateur</th>
                          <th>Pays</th>
                          <th className="text-center">Followers</th>
                          <th className="text-center">
                            {process.env.REACT_APP_SOCIAL_HUB
                              ? "Contacts & Abonnés"
                              : "Potes & Fans"}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadingMostFollowed ? (
                          <tr>
                            <td
                              colSpan="5"
                              className="text-center text-muted py-4"
                            >
                              <i
                                className="fa fa-spinner fa-spin mr-2"
                                aria-hidden="true"
                              />
                              Chargement du top des utilisateurs…
                            </td>
                          </tr>
                        ) : mostFollowedUsers.length > 0 ? (
                          mostFollowedUsers.map((obj) => (
                            <tr
                              key={obj?.user_id}
                              onClick={() => navigateToUserProfile(obj)}
                              style={{ cursor: "pointer" }}
                            >
                              <td className="w60">
                                <img
                                  src={obj?.prof_picture || MyOpinionLogo}
                                  data-toggle="tooltip"
                                  data-placement="top"
                                  alt={`@(${obj?.user_username})`}
                                  className="w35 rounded"
                                />
                              </td>
                              <td>
                                {getUserFullName(obj, true)}{" "}
                                {obj.user_verified && (
                                  <i
                                    style={{ cursor: "pointer" }}
                                    title="Verified"
                                    className="fa fa-check-circle-o text-primary mr-1"
                                  />
                                )}
                                {obj.user_gold && (
                                  <i
                                    style={{ cursor: "pointer" }}
                                    title="Premium / Gold"
                                    className="fa fa-star text-warning mr-1"
                                  />
                                )}
                              </td>
                              <td>
                                {obj?.ip_country_flag &&
                                  obj?.ip_country_name && (
                                    <>
                                      <img
                                        src={obj?.ip_country_flag}
                                        data-toggle="tooltip"
                                        data-placement="top"
                                        alt={obj?.ip_country_name}
                                        className="w20 rounded"
                                      />{" "}
                                    </>
                                  )}
                                {obj?.ip_country_name || "-"}
                              </td>
                              <td
                                className={`text-center ${
                                  (obj?.all_followers_count || 0) > 1000
                                    ? "text-success"
                                    : "text-danger"
                                }`}
                              >
                                {formatCash(obj?.all_followers_count || 0)}
                              </td>
                              <td
                                className="text-center"
                                title={numberWithSeparator(
                                  obj?.total_followers || 0
                                )}
                              >
                                {formatCash(obj?.total_followers || 0)}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="text-center text-muted">
                              Aucun utilisateur à afficher.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <div className="row clearfix">
              <div className="col-md-12">
                <div className="card">
                  <div className="header font-weight-bold">
                    <h2>Top des comptes monétisés</h2>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-hover table-custom spacing8">
                      <thead>
                        <tr>
                          <th>Photo</th>
                          <th>Utilisateur</th>
                          <th className="text-center">Followers</th>
                          <th className="text-center">Coins (USD)</th>
                          <th className="text-center">Likes (USD)</th>
                          <th className="text-center">Vues (USD)</th>
                          <th className="text-center">Total (USD)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fetchingMonetizedAccounts ? (
                          <tr>
                            <td colSpan="7" className="text-center text-muted">
                              <i
                                className="fa fa-spinner fa-spin mr-2"
                                aria-hidden="true"
                              />
                              Chargement des comptes monétisés…
                            </td>
                          </tr>
                        ) : monetizedAccounts &&
                          monetizedAccounts.length > 0 ? (
                          monetizedAccounts.map((row, index) => {
                            const user = row.user || {};
                            const balances = row.balances || {};
                            const coins = balances.coins || {};
                            const likes = balances.likes || {};
                            const views = balances.views || {};

                            return (
                              <tr
                                key={user.user_id || index}
                                onClick={() => navigateToUserProfile(user)}
                                style={{ cursor: "pointer" }}
                              >
                                <td className="w60">
                                  <img
                                    src={user.prof_picture || MyOpinionLogo}
                                    alt={`@(${user.user_username})`}
                                    className="w35 rounded"
                                    style={{ objectFit: "cover" }}
                                  />
                                </td>
                                <td>
                                  {getUserFullName(user, true)}{" "}
                                  {user.user_verified && (
                                    <i
                                      style={{ cursor: "pointer" }}
                                      title="Verified"
                                      className="fa fa-check-circle-o text-primary mr-1"
                                    />
                                  )}
                                  {user.user_gold && (
                                    <i
                                      style={{ cursor: "pointer" }}
                                      title="Premium / Gold"
                                      className="fa fa-star text-warning mr-1"
                                    />
                                  )}
                                </td>
                                <td
                                  className={`text-center font-weight-bold ${
                                    (row.all_followers_count || 0) > 1000
                                      ? "text-success"
                                      : "text-danger"
                                  }`}
                                >
                                  {formatCash(row.all_followers_count || 0)}
                                </td>
                                <td className="text-center">
                                  {formatCash(coins.usd || 0)} USD (
                                  {formatCash(coins.amount || 0)})
                                </td>
                                <td className="text-center">
                                  {formatCash(likes.usd || 0)} USD (
                                  {formatCash(likes.amount || 0)})
                                </td>
                                <td className="text-center">
                                  {formatCash(views.usd || 0)} USD (
                                  {formatCash(views.amount || 0)})
                                </td>
                                <td className="text-center text-success font-weight-bold">
                                  {formatCash(balances.total_usd || 0)} USD
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="7" className="text-center">
                              Aucun compte monétisé trouvé.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  authUser: state.auth.user,
});

const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
